import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  ShieldCheck,
  User,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  XCircle,
  ArrowLeft,
} from "lucide-react";
import { useLoginHooks } from "@/hooks/useLoginHooks";

/** What the console is for — surfaced on the brand panel. Keys already exist. */
const CONSOLE_SCOPE = ["Dashboard", "User Management", "Societies", "Sponsors"];

/**
 * Dedicated admin console login at `/console/login`. Reuses the admin login
 * handler from `useLoginHooks` (lands on `/console/dashboard`).
 *
 * The handler signals its in-flight request through `app.showLoading`
 * (`controlLoading`), so that flag is read here as the single source of truth
 * for the button's pending state — no second, page-local request state.
 */
const ConsoleLoginPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    email,
    setEmail,
    password,
    setPassword,
    showValidation,
    modalAlertText,
    setModalAlertText,
    hideModalAlert,
    handleAdminLogin,
  } = useLoginHooks("admin");
  const pending = useSelector((state: any) => Boolean(state.app.showLoading));
  const [showPassword, setShowPassword] = useState(false);

  // `showValidation` flips on the first submit attempt, which is what turns the
  // blank-field checks into visible field-level errors.
  const usernameInvalid = showValidation && !email.trim();
  const passwordInvalid = showValidation && !password;
  const formError = modalAlertText.trim();

  /** Editing after a failed attempt clears the inline error straight away. */
  const dismissError = () => {
    hideModalAlert();
    if (formError) setModalAlertText("");
  };

  const handleChange =
    (setter: (value: string) => void) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dismissError();
      setter(event.target.value);
    };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Belt-and-braces alongside `disabled={pending}`: a submit that arrives
    // while a request is already in flight is dropped rather than queued.
    if (pending) return;
    dismissError();
    await handleAdminLogin();
  };

  const inputClass = (invalid: boolean, hasToggle = false) =>
    `w-full rounded-xl border bg-[#fff3e0]/40 py-3 pl-10 text-sm text-[#1f2937] placeholder:text-gray-400 transition-all focus:bg-white focus:outline-none ${
      hasToggle ? "pr-11" : "pr-3.5"
    } ${
      invalid
        ? "border-red-400 focus:border-red-400 focus:ring-2 focus:ring-red-200"
        : "border-gray-200 hover:border-gray-300 focus:border-[#ffa217] focus:ring-2 focus:ring-[#ffa217]/30"
    }`;

  const fieldError = (invalid: boolean, id: string) =>
    invalid ? (
      <p id={id} className="mt-1.5 flex items-center gap-1 text-[13px] text-red-500">
        <XCircle size={14} className="shrink-0" />
        {t("Please fill in all the blank")}
      </p>
    ) : null;

  return (
    <div className="flex min-h-screen bg-white">
      {/* Brand panel — desktop only */}
      <aside className="relative hidden w-[46%] shrink-0 flex-col justify-between overflow-hidden bg-gradient-to-br from-[#1f2937] via-[#241d16] to-[#120e08] p-10 lg:flex xl:w-1/2 xl:p-14">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-20 top-1/4 h-72 w-72 rounded-full bg-[#ffa217]/10 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-24 -right-16 h-80 w-80 rounded-full bg-[#ffa217]/[0.07] blur-3xl"
        />

        <Link
          to="/"
          className="relative z-10 inline-flex w-fit items-center gap-2.5 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffa217] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1f2937]"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-[#ffa217] ring-1 ring-inset ring-white/15">
            <ShieldCheck size={20} />
          </span>
          <span className="text-xl font-extrabold tracking-tight text-white">
            Uninfo<span className="text-[#ffa217]">HK</span>
          </span>
        </Link>

        <div className="relative z-10 max-w-md">
          <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10 text-[#ffa217] ring-1 ring-inset ring-white/15">
            <ShieldCheck size={30} />
          </span>
          <h2 className="mt-6 text-3xl font-bold leading-tight text-white">
            {t("Administration, in one place.")}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-white/60">
            {t("Review students, societies and sponsors across the Uninfo platform.")}
          </p>
          <ul className="mt-8 space-y-2.5">
            {CONSOLE_SCOPE.map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm text-white/65">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#ffa217]" />
                {t(item)}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative z-10 flex items-center gap-4 text-xs text-white/40">
          <Link
            to="/terms"
            className="rounded transition-colors hover:text-white/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffa217]"
          >
            {t("Terms")}
          </Link>
          <span aria-hidden="true">·</span>
          <Link
            to="/privacy"
            className="rounded transition-colors hover:text-white/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffa217]"
          >
            {t("Privacy")}
          </Link>
        </div>
      </aside>

      {/* Form panel */}
      <main className="flex flex-1 items-center justify-center px-5 py-10 sm:px-8 lg:px-12">
        <div className="w-full max-w-[26rem]">
          {/* Mobile brand */}
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#fff3e0] text-[#ffa217]">
              <ShieldCheck size={20} />
            </span>
            <span className="text-xl font-extrabold text-[#1f2937]">
              Uninfo<span className="text-[#ffa217]">HK</span>
            </span>
          </div>

          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#ffa217]">
            {t("Admin Console")}
          </p>
          <h1 className="mt-2 text-2xl font-bold text-[#1f2937]">
            {t("Sign in to access your account")}
          </h1>
          <p className="mt-1.5 text-sm text-gray-500">
            {t("Authorised administrators only.")}
          </p>

          <form className="mt-8 space-y-5" onSubmit={handleSubmit} noValidate>
            <div>
              <label
                htmlFor="console-username"
                className="mb-1.5 block text-sm font-medium text-gray-700"
              >
                {t("Username")}
              </label>
              <div className="relative">
                <User
                  size={18}
                  className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  id="console-username"
                  type="text"
                  value={email}
                  onChange={handleChange(setEmail)}
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className={inputClass(usernameInvalid)}
                  placeholder={t("Username")}
                  aria-invalid={usernameInvalid}
                  aria-describedby={
                    usernameInvalid ? "console-username-error" : undefined
                  }
                />
              </div>
              {fieldError(usernameInvalid, "console-username-error")}
            </div>

            <div>
              <label
                htmlFor="console-password"
                className="mb-1.5 block text-sm font-medium text-gray-700"
              >
                {t("Password")}
              </label>
              <div className="relative">
                <Lock
                  size={18}
                  className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  id="console-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={handleChange(setPassword)}
                  autoComplete="current-password"
                  className={inputClass(passwordInvalid, true)}
                  placeholder="••••••••"
                  aria-invalid={passwordInvalid}
                  aria-describedby={
                    passwordInvalid ? "console-password-error" : undefined
                  }
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? t("Hide password") : t("Show password")}
                  aria-pressed={showPassword}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded text-gray-400 transition-colors hover:text-[#ffa217] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffa217]/50"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {fieldError(passwordInvalid, "console-password-error")}
            </div>

            {formError && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-[13px] text-red-600"
              >
                <XCircle size={16} className="mt-px shrink-0" />
                <span>{formError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={pending}
              aria-busy={pending}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#ffa217] py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#e08f0e] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffa217] focus-visible:ring-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-[#ffc768] disabled:text-white/90 disabled:shadow-none"
            >
              {pending && <Loader2 size={16} className="animate-spin" />}
              {pending ? t("Signing in...") : t("Login")}
            </button>
          </form>

          <div className="mt-8 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded text-sm text-gray-400 transition-colors hover:text-[#ffa217] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffa217]/50"
            >
              <ArrowLeft size={14} />
              {t("Back to Home")}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ConsoleLoginPage;
